// @school-apps/core/exams — exam awareness (E·1): the anti-all-nighter feature that CONSUMES the
// first-class exm_ records (Y·2). Two PURE capabilities, no I/O and no writes:
//   1. upcomingExams — a countdown to each future exam (sorted, with daysUntil; past exams excluded).
//   2. examRamp — inside a window before an exam, a PRESENTATION ordering that surfaces that exam
//      course's weakest cards first. It RE-ORDERS, it never inflates: the session size is unchanged,
//      the queue is only reshuffled. And it is zero-double-scheduling — it NEVER mutates FSRS state,
//      never marks a card due early, never touches srs. A card not due stays not due (the ramp only
//      ever reorders cards the caller already handed it — its due review queue).
//
// This is the core/lib/pacing precedent (A·2): pure calendar arithmetic + a reordering over
// already-synced data, bundled CLIENT-side (a subpath export) so an app's dashboard renders straight
// from it — never wrong, only early. It lives in core because all three kids get exam awareness; the
// countdown in particular stands ALONE for an app with zero schedule (Dylan has no srs — a countdown
// must never require the ramp).
//
// Weakness is the EXISTING FSRS weak-signal seam, not a second notion: a card is weak exactly when its
// srs-derived `ease` is below WEAK_EASE (the same threshold flagWeakTopics uses per card). Reuse, don't
// reinvent. A card links to an exam via its COURSE (deck ↔ course is already linked) — each input card
// carries the `courseId` its deck resolves to; there is no card↔exam relation.
import { WEAK_EASE } from "../ai/weak.js";

/**
 * The ramp window: an exam prioritizes its course's weak cards starting this many days out. A named
 * constant, per the decision — a later lesson can add a per-exam override; the constant is the default.
 */
export const RAMP_WINDOW_DAYS = 7;

/** The date-only portion (YYYY-MM-DD) of a date-only string OR a full ISO timestamp. */
const dayOf = (iso) => String(iso ?? "").slice(0, 10);
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Whole calendar days from `a` to `b` (date-only ISO), b - a — the pacing (A·2) arithmetic. */
const daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

/** Stable tie-break for equal sort keys: ascending id. */
const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * The countdown: every FUTURE exam with a whole-calendar-day `daysUntil`, nearest first; past exams
 * excluded. PURE — no srs, no writes — so it stands ALONE for an app with zero schedule (Dylan-safe).
 * @param {Array<{ id: string, courseId: string, title: string, date?: string, type?: string }>} exams
 * @param {string} now  local date-only ISO (YYYY-MM-DD) OR a full ISO timestamp — only the date is read
 * @returns {Array<{ ...exam, daysUntil: number }>}  daysUntil >= 0 (today counts), nearest first
 */
export function upcomingExams(exams, now) {
  const today = dayOf(now);
  return (Array.isArray(exams) ? exams : [])
    .filter((e) => e && typeof e.date === "string" && DATE_RE.test(e.date))
    .map((e) => ({ ...e, daysUntil: daysBetween(today, e.date) }))
    .filter((e) => e.daysUntil >= 0) // past exams excluded; an exam TODAY still counts
    .sort((a, b) => a.daysUntil - b.daysUntil || byId(a, b));
}

/**
 * The pre-exam ramp: for each exam inside the window, the ordered set of that exam course's WEAKEST
 * card ids (from the caller's already-due queue), weakest first — plus the exam it serves.
 *
 * ZERO double-scheduling, by construction: this function reads `ease` off srs and NOTHING else — it
 * never mutates srs, never sets a due date, never marks a card due early. Its output is always a subset
 * of the `cards` it was given (the due review queue), so a card the caller did not pass — a card not
 * due — can never surface. Pass the DUE queue as `cards` and the ramp only reshuffles it.
 *
 * @param {object} arg
 * @param {Array<{ id: string, courseId: string, title: string, date?: string }>} arg.exams  the exm_ records
 * @param {Array<{ id: string, courseId: string, tags?: string[] }>} arg.cards  the ALREADY-DUE review
 *   queue; each card carries the `courseId` its deck resolves to (deck ↔ course, no new relation)
 * @param {Map<string,{ease:number}>|Record<string,{ease:number}>} arg.srs  cardId → srs state (read-only;
 *   `ease` is the only field read). A card with no entry has no ease → it is not "weak".
 * @param {string} arg.now  local date-only ISO or ISO timestamp — only the date is read (for the window)
 * @param {number} [arg.windowDays]  ramp window in days (default RAMP_WINDOW_DAYS; the per-exam override seam)
 * @returns {Array<{ exam: { ...exam, daysUntil: number }, cardIds: string[] }>}  one entry per in-window
 *   exam, nearest first; `cardIds` = that course's weak due cards, weakest (lowest ease) first
 */
export function examRamp({ exams, cards, srs, now, windowDays = RAMP_WINDOW_DAYS } = {}) {
  const inWindow = upcomingExams(exams, now).filter((e) => e.daysUntil <= windowDays);
  if (inWindow.length === 0) return []; // no exam in the window -> empty ramp, the normal queue is untouched

  const easeOf = (id) => {
    const st = srs instanceof Map ? srs.get(id) : (srs && typeof srs === "object" ? srs[id] : undefined);
    return typeof st?.ease === "number" ? st.ease : undefined;
  };
  const cardList = Array.isArray(cards) ? cards : [];

  return inWindow.map((exam) => {
    const weak = cardList
      .filter((c) => c && c.courseId === exam.courseId)
      .map((c) => ({ id: c.id, ease: easeOf(c.id) }))
      // WEAK_EASE is the SAME threshold the weak-topic system uses — no second notion of "weak".
      .filter((c) => typeof c.ease === "number" && c.ease < WEAK_EASE)
      .sort((a, b) => a.ease - b.ease || byId(a, b)); // weakest (lowest ease) first, ties by id
    return { exam, cardIds: weak.map((c) => c.id) };
  });
}
